import textingService from '@/modules/texting/services/textingService';

const state = {
  textingSettings: null,
  activeDedicatedNumber: null,
  isLoading: false,
};

const getters = {
  textingSettings: (state) => state.textingSettings,
  activeDedicatedNumber: (state) => state.activeDedicatedNumber,
  isLoading: (state) => state.isLoading,
  promotionalVerificationStatus: (state) => state.activeDedicatedNumber?.promotional_verification_status || null,
  campaignSms: (state) => state.activeDedicatedNumber?.verification_request_data?.campaign_sms || false,
  campaignExample: (state) => state.activeDedicatedNumber?.verification_request_data?.campaign_example || '',
};

const mutations = {
  setTextingSettings(state, settings) {
    state.textingSettings = settings;
  },
  setActiveDedicatedNumber(state, activeDedicatedNumber) {
    state.activeDedicatedNumber = activeDedicatedNumber;
  },
  setIsLoading(state, isLoading) {
    state.isLoading = isLoading;
  },
};

const actions = {
  // currenty country code is hardcoded to US, but we should make it dynamic
  async getAvailablePhoneNumbers(context, countryCode = 'US') {
    const response = await textingService.getAvailablePhoneNumbers(countryCode);
    return response.data.available_phone_numbers;
  },

  async claimPhoneNumber({ commit }, { phoneNumber, countryCode = 'US', verificationRequestData = null }) {
    const response = await textingService.claimPhoneNumber(phoneNumber, countryCode, verificationRequestData);

    // Update the active dedicated number with the claimed number
    const activeDedicatedNumber = {
      phone_number: phoneNumber,
      country_code: countryCode,
      features: ['SMS'],
    };

    commit('setActiveDedicatedNumber', activeDedicatedNumber);

    // Also update texting settings
    const textingSettings = {
      dedicated_number: phoneNumber,
      texting_enabled: true,
      app_status: 'active',
    };

    commit('setTextingSettings', textingSettings);

    return response;
  },

  async fetchActiveDedicatedNumber({ commit }, params) {
    try {
      const activeDedicatedNumber = await textingService.getActiveTextingPhoneNumber(params);
      commit('setActiveDedicatedNumber', activeDedicatedNumber);
      return activeDedicatedNumber;
    } catch (error) {
      console.error('Error fetching active texting phone number:', error);
      return null;
    }
  },

  async fetchTextingSettings({ commit, dispatch }) {
    commit('setIsLoading', true);
    try {
      // Fetch the active dedicated number
      const activeDedicatedNumber = await dispatch('fetchActiveDedicatedNumber');

      // Create basic settings from the dedicated number info
      let settings = null;
      if (activeDedicatedNumber) {
        await dispatch('CallsTextingStore/fetchCallsSettings', activeDedicatedNumber.phone_number, { root: true });
        settings = {
          dedicated_number: activeDedicatedNumber.phone_number,
          texting_enabled: activeDedicatedNumber.features.includes('SMS'),
          app_status: 'active',
        };
      }

      commit('setTextingSettings', settings);
      return settings;
    } catch (error) {
      console.error('Error fetching texting settings:', error);
      return null;
    } finally {
      commit('setIsLoading', false);
    }
  },

  async setTextingStatus({ commit, state }, status) {
    const { activeDedicatedNumber } = state;
    if (activeDedicatedNumber) {
      const updatedNumber = { ...activeDedicatedNumber, status };
      commit('setActiveDedicatedNumber', updatedNumber);
    }
  },

  setIsLoading({ commit }, isLoading) {
    commit('setIsLoading', isLoading);
  },

  async updateVerificationData({ commit, state }, { uid, verificationRequestData }) {
    const resolvedUid = uid || state.activeDedicatedNumber?.uid;
    if (!resolvedUid) {
      throw new Error('No active dedicated number found');
    }
    const response = await textingService.updateBusinessPhoneNumber(resolvedUid, {
      verification_request_data: verificationRequestData,
    });
    if (state.activeDedicatedNumber?.uid === resolvedUid) {
      commit('setActiveDedicatedNumber', {
        ...state.activeDedicatedNumber,
        promotional_verification_status: response?.promotional_verification_status,
        verification_request_data: verificationRequestData,
      });
    }
    return response;
  },

  // Used when no verification_request_data exists yet (old businesses).
  // Saves the full verification form data together with the campaign settings in a single PUT.
  async updateVerificationDataWithCampaignSms({ commit, state }, { uid, verificationData, campaignSms, campaignExample }) {
    const resolvedUid = uid || state.activeDedicatedNumber?.uid;
    if (!resolvedUid) {
      throw new Error('No active dedicated number found');
    }

    const verificationRequestData = {
      ...verificationData,
      campaign_sms: campaignSms,
      campaign_example: campaignExample,
    };

    const response = await textingService.updateBusinessPhoneNumber(resolvedUid, {
      verification_request_data: verificationRequestData,
    });

    if (state.activeDedicatedNumber?.uid === resolvedUid) {
      commit('setActiveDedicatedNumber', {
        ...state.activeDedicatedNumber,
        promotional_verification_status: response.promotional_verification_status,
        verification_request_data: verificationRequestData,
      });
    }
    return response;
  },

  async updateCampaignSmsSettings({ commit, state }, { campaignSms, campaignExample }) {
    const { activeDedicatedNumber } = state;
    if (!activeDedicatedNumber?.uid) {
      throw new Error('No active dedicated number found');
    }

    // Merge existing verification data with campaign updates
    const existingVerificationData = activeDedicatedNumber.verification_request_data || {};
    const response = await textingService.updateBusinessPhoneNumber(activeDedicatedNumber.uid, {
      verification_request_data: {
        ...existingVerificationData,
        campaign_sms: campaignSms,
        campaign_example: campaignExample,
      },
    });

    // Update local state with the new values
    const updatedNumber = {
      ...activeDedicatedNumber,
      promotional_verification_status: response.promotional_verification_status,
      verification_request_data: {
        ...existingVerificationData,
        campaign_sms: campaignSms,
        campaign_example: campaignExample,
      },
    };

    commit('setActiveDedicatedNumber', updatedNumber);
    return response;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
